/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ArticlesAction from "store/articles/ArticlesAction";
import {PersonType} from "types/enums/PersonType";
import {ArticleGroup} from "types/entities/ArticleGroup";
import {Article} from "types/entities/Article";
import {ArticlePersonsType} from "types/entities/ArticlePersonsType";
import {ArticleType} from "types/enums/ArticleType";
import {ArticleTicketType} from "types/enums/ArticleTicketType";

export interface ArticlesReducerSetArticlesAction {
    payload: Article[];
    type: typeof ArticlesAction.SET_ARTICLES;
}

export interface ArticlesReducerSetArticlesKioskAction {
    payload: Article[];
    type: typeof ArticlesAction.SET_ARTICLES_KIOSK;
}

export interface ArticlesReducerSetArticleGroupsAction {
    payload: ArticleGroup[];
    type: typeof ArticlesAction.SET_ARTICLE_GROUPS;
}

type ArticlesReducerAction =
    ArticlesReducerSetArticlesAction
    | ArticlesReducerSetArticlesKioskAction
    | ArticlesReducerSetArticleGroupsAction;

export interface ArticlesReducerState {
    all: Article[];
    online: ArticlePersonsType;
    action: ArticlePersonsType;
    vouchers: Article[];
    yearly: Article[];
    threetwo: Article[];
    articlesKiosk: Article[];
    articleGroups: ArticleGroup[];
}

class ArticlesReducer {
    initialState: ArticlesReducerState = {
        all: [],
        online: {
            adult: [],
            child: [],
        },
        action: {
            adult: [],
            child: [],
        },
        vouchers: [],
        yearly: [],
        threetwo: [],
        articlesKiosk: [],
        articleGroups: [],
    };

    reducer = (state: ArticlesReducerState = this.initialState, action: ArticlesReducerAction): ArticlesReducerState => {
        switch (action.type) {
            case ArticlesAction.SET_ARTICLES:
                const articles: Article[] = action.payload as Article[];
                if (!articles) {
                    return state;
                }
                const onlineTickets = articles.filter(article => article.type === ArticleType.Online);
                const actionTickets = articles.filter(article => article.type === ArticleType.Action);
                const voucherTickets = articles.filter(article => {
                    return article.type === ArticleType.Online && article.ticket_type === ArticleTicketType.Voucher;
                });

                const yearlyTickets = articles.filter(article => {
                    return article.type === ArticleType.Yearly && article.ticket_type === ArticleTicketType.Voucher;
                });

                 const threeTwoTickets = articles.filter(article => {
                    return article.type === ArticleType.ThreeTwo && article.ticket_type === ArticleTicketType.Voucher;
                });

                //checks null or undefined, not empty arrays
                if (!onlineTickets || !actionTickets || !voucherTickets || !yearlyTickets) {
                    return state;
                }

                return <ArticlesReducerState>{
                    ...state,
                    all: action.payload,
                    online: {
                        ...state.online,
                        adult: onlineTickets?.filter(article => article.person_type === PersonType.Adult),
                        child: onlineTickets?.filter(article => article.person_type === PersonType.Child),
                    },
                    action: {
                        ...state.action,
                        adult: actionTickets?.filter(article => article.person_type === PersonType.Adult),
                        child: actionTickets?.filter(article => article.person_type === PersonType.Child),
                    },
                    vouchers: voucherTickets,
                    yearly: yearlyTickets,
                    threetwo: threeTwoTickets
                };


            case ArticlesAction.SET_ARTICLES_KIOSK:
                return <ArticlesReducerState>{
                    ...state,
                    articlesKiosk: action.payload,
                };

            case ArticlesAction.SET_ARTICLE_GROUPS:
                return <ArticlesReducerState>{
                    ...state,
                    articleGroups: action.payload,
                };

            default:
                return state;
        }
    };
}

export default ArticlesReducer;
